import { GetterTree } from 'vuex';
import State from '@/store/state';
import {
  Category,
  Department,
  Institution,
  LearningObject,
  Study,
  Survey,
  GetMostPopularProceduresResponse,
  GetInfrequentProceduresResponse,
  GetNursesExperienceResponse,
  GetNurseAgeExperienceStatsResponse,
  Patient,
  GetNurseAgeStatsResponse,
  GetNurseExperienceStatsResponse,
  GetAverageShiftDataResponse,
  GetAveragePatientAcuityDataResponse,
  PatientVisit,
} from '@/generated/graphql';

import { ProcedureDetailItem, ProcedureListItem, ProcedureStep } from '@/@types/procedures';
import { InstitutionListItem } from '@/@types/institutions';
import { StudyDetailItem, StudyListItem, StudyResultListItem } from '@/@types/study';
import { DateDisplay } from '@/@types/general';
import { LearningObjecsListItem } from '@/@types/learningObjects';
import { useI18n } from 'vue-i18n';
import { PatientVisitListItem } from '@/@types/visits';
import { formatNurseName } from '@/utils';
// TODO move to separate helper utils

const helpers = {
  getDateAdded: (dbDateTime): DateDisplay => {
    if (!dbDateTime) {
      return {
        name: '-',
        helped: '-',
        date: null,
      };
    }
    const date = new Date(dbDateTime);
    return {
      name: `${date.toLocaleString('default', {
        month: 'short',
      })} ${date.getDate()}, ${date.getFullYear()}`,
      helped: `${date.toLocaleString('en-US', {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
      })}`,
      date,
    };
  },
};

const getters: GetterTree<State, any> = {
  proceduresList(state): Array<Omit<ProcedureListItem, 'public'> & { public: string }> {
    const procedures = state.procedures;
    if (!procedures.length) {
      return [];
    }
    return procedures.map((procedure) => {
      const { t } = useI18n();
      const isPublic = procedure.isPublic ? t('procedures.public') : t('procedures.private');
      return {
        id: procedure.id,
        code: procedure.code,
        name: procedure.name,
        public: isPublic,
        department: procedure.departments.map((department: Department) => department.name).join(','),
        category: procedure.category?.name,
        dateAdded: helpers.getDateAdded(procedure.createdAt),
        notActive: procedure.isEnabled,
      };
    });
  },
  activeProcedure(state): ProcedureDetailItem {
    const activeProcedure = state.activeProcedure;
    if (!activeProcedure) {
      return null;
    }
    const steps = [].concat(activeProcedure.steps);
    return {
      id: activeProcedure.id,
      code: activeProcedure.code,
      name: activeProcedure.name,
      description: activeProcedure.description,
      category: activeProcedure.category.name,
      categoryId: activeProcedure.category.id,
      departmentId: activeProcedure.departments[0].id,
      departmentName: activeProcedure.departments[0].name,
      institutionName: activeProcedure.departments[0].institution.name,
      author: activeProcedure.author?.name || '',
      owner: activeProcedure.departments.map((item) => item.institution.name).join(','),
      language: activeProcedure.language,
      public: activeProcedure.isPublic,
      checklist: activeProcedure.checklist,
      steps: steps.map((step) => {
        const displayName = step.title.replace(/<[^>]+>/g, '');

        const { errors, ...rest } = step;
        const stepTitle = {
          name: step.title,
          helped: errors
            ?.map((error) => {
              return error.error;
            })
            .join(','),
        };
        const stepDetails: ProcedureStep = Object.assign({}, rest, {
          title: stepTitle,
          displayName,
        });
        return stepDetails;
      }),
      dateAdded: helpers.getDateAdded(activeProcedure.createdAt),
      dateUpdated: helpers.getDateAdded(activeProcedure.updateAt),
      notActive: activeProcedure.isEnabled,
    };
  },

  categories(state): Array<Category> {
    return state.categoryList;
  },

  activeDepartmentId(state): string {
    if (state.currentRoles && state.currentRoles.length) {
      return state.currentRoles[0].departmentId;
    } else {
      return null;
    }
  },

  activeDepartmentName(state): string {
    if (state.currentRoles && state.currentRoles.length) {
      return state.currentRoles[0].departmentName;
    }
  },

  activeInstitutionId(state): string {
    if (state.currentRoles && state.currentRoles.length) {
      return state.currentRoles[0]?.institutionId;
    } else {
      return null;
    }
  },

  activeInstitutionName(state): string {
    if (state.currentRoles && state.currentRoles.length) {
      return state.currentRoles[0]?.institutionName;
    } else {
      return '';
    }
  },

  currentUserRoles(state): string[] {
    if (state.currentRoles && state.currentRoles.length) {
      // return ['end_user_nurse']
      return state.currentRoles.map((r) => r.role);
    } else {
      return [];
    }
  },

  currentUserId(state): string[] {
    return state.currentUser.id;
  },

  institutionList(state: State): InstitutionListItem[] {
    const institutions: Institution[] = state.institutions;

    return institutions.map((item: Institution) => {
      return {
        id: item.id,
        name: item.name,
        street: item.street,
        house: item.house,
        city: item.city,
        county: item.county,
        country: item.country,
        timezone: item.timezone,
        language: item.language,
        region: item.country,
        contact: item.administrators.map((a) => a.name).join(', ') || '-',
        dateAdded: helpers.getDateAdded(item.createdAt),
        stopDetectionDuration: item.stopDetectionDuration,
        deletedAt: item.deletedAt,
      };
    });
  },

  acuityDataStats(state): GetAveragePatientAcuityDataResponse {
    const acuityStats = state.acuityAverageStats;
    return acuityStats;
  },
  shiftDataStats(state): GetAverageShiftDataResponse {
    const stats = state.shiftAverageStats;
    return stats;
  },
  mostPopularProcedures(state): GetMostPopularProceduresResponse {
    const proceduresStats = state.mostPopularProcedures;
    return proceduresStats;
  },
  infrequentsProcedures(state): GetInfrequentProceduresResponse {
    const proceduresStats = state.infrequentsProcedures;
    return proceduresStats;
  },
  fiveMostFrequentlyProcedures(state): any[] {
    const proceduresStats = state.fiveMostFrequentlyProcedures;
    const mappedProcedures =
      proceduresStats?.map((el) => ({
        ...el.procedure,
        count: el.numberOfExecutions,
      })) || [];
    return mappedProcedures;
  },
  fiveLeastFrequentlyProcedures(state): any[] {
    const proceduresStats = state.fiveLeastFrequentlyProcedures;
    const mappedProcedures =
      proceduresStats?.map((el) => ({
        ...el.procedure,
        count: el.numberOfExecutions,
      })) || [];
    return mappedProcedures;
  },
  fiveMostAssistedProcedures(state): any {
    const procedureStats = state.fiveMostAssistedProcedures;
    const mappedProcedures =
      procedureStats?.map((el) => ({
        ...el.procedure,
        count: el.numberOfSuggestedAssistances,
      })) || [];
    return mappedProcedures;
  },
  nursesExperience(state): GetNursesExperienceResponse {
    const nursesStats = state.nursesExperience;
    return nursesStats;
  },
  nursesAgeExperience(state): GetNurseAgeExperienceStatsResponse {
    const nursesStats = state.nurseAgeExperienceStats;
    return nursesStats;
  },
  nursesAge(state): GetNurseAgeStatsResponse {
    const nursesStats = state.nurseAgeStats;
    return nursesStats;
  },
  nursesSex(state): GetNurseExperienceStatsResponse {
    const nursesStats = state.nurseSexStats;
    return nursesStats;
  },
  studyList(state): StudyListItem[] {
    const studies: Study[] = state.studies;

    return studies.map((item) => {
      return {
        id: item.id,
        name: item.name,
        department: item.department?.name || '',
        status: item.status,
        start: helpers.getDateAdded(item.startsAt),
        end: helpers.getDateAdded(item.endsAt),
        dateAdded: helpers.getDateAdded(item.createdAt),
      };
    });
  },
  activeStudy(state): StudyDetailItem {
    const item: Study = state.activeStudy;
    if (!item) {
      return null;
    }

    return {
      id: item.id,
      name: item.name,
      description: item.description,
      survey: item.survey,
      departmentId: item.department.id,
      institutionName: item.department?.institution?.name || '',
      permission: item.permission,
      information: {
        author: item.author?.name || '',
        owner: item.department?.name || '',
        duration: {
          start: helpers.getDateAdded(item.startsAt),
          end: helpers.getDateAdded(item.endsAt),
        },
        status: item.status,
      },
    };
  },
  getPaginationShowSize(state): number {
    return state.tableShowSize;
  },
  learningObjectsList(state): LearningObjecsListItem[] {
    const learningObjects: LearningObject[] = state.learningObjectLists;
    return learningObjects.map((item) => {
      return {
        id: item.id,
        name: item.name,
        isEnabled: item.isEnabled,
        department: item.departments.map((department: Department) => department.name).join(','),
        dateAdded: helpers.getDateAdded(item.createdAt),
        notActive: item.isEnabled,
      };
    });
  },

  studyResultsList(state): StudyResultListItem[] {
    const surveys: Survey[] = state.studyResults;
    return surveys.map((survey) => ({
      id: survey.id,
      studyName: survey.study?.name || '',
      departmentName: survey.study?.department?.name || '',
      surveyName: survey.name || '-',
      startDate: helpers.getDateAdded(survey.startsAt),
      endDate: helpers.getDateAdded(survey.endsAt),
      answerCount: survey.results?.length || 0,
    }));
  },

  surveyResultsList: (state) => (surveyId) => {
    try {
      let studyResults: Study[];
      if (Object.keys(state.studies).length) {
        studyResults = JSON.parse(JSON.stringify(state.studies));
      } else {
        studyResults = JSON.parse(localStorage.getItem('studyResultsList'));
      }
      const survey: Survey[][] = studyResults.map((study) => {
        return study.survey.filter((survey) => survey.id === surveyId);
      });
      const data: any = survey.flat(1)[0];
      console.log(data);
      data.results = data.results.map((result) => {
        const { date, ...rest } = result;
        return {
          id: rest.user.id,
          user: rest.user.name,
          date: helpers.getDateAdded(date),
          answers: rest.answers,
        };
      });
      data.results = data.results.sort((a, b) => b.date.date - a.date.date);

      return data;
    } catch (e) {
      return {};
    }
  },
  userResultsList: (state) => (resultId) => {
    try {
      const studyResults: Study[] = JSON.parse(JSON.stringify(state.studies));
      console.log('FULL STUDY', JSON.stringify(studyResults));
      const result = studyResults.map((study) => {
        return study.survey.map((survey) => {
          return survey.results.filter((results) => results.id === resultId);
        });
      });
      console.log('RESULT', result);
      const data = result.flat(2)[0];
      data.date = helpers.getDateAdded(data.date);
      console.log('GETTER DATA', JSON.stringify(data));
      data.answers = data.answers.sort((a, b) => {
        const positionA = a.position || 0;
        const positionB = b.position || 0;
        return positionA - positionB;
      });
      return data;
    } catch (e) {
      return null;
    }
  },
  surveyResults(state): any {
    return state.surveyResults?.items?.[0].answers;
  },
  shiftIntensityChartData(state): any {
    const data = state.shiftIntensityChart.data;
    return data;
  },
  shiftTirednessChartData(state): any {
    const data = state.shiftTirednessChart.data;
    return data;
  },
  institutionPageInfo(state) {
    const pageInfo = state.institutionPageInfo;
    return pageInfo;
  },
  patientsPageInfo(state) {
    const pageInfo = state.patientsPageInfo;
    return pageInfo;
  },
  patientsList(state, getters, rootState, rootGetters) {
    const patients = state.patientsList;
    const { t } = useI18n();
    return patients.map((patient: Patient) => {
      const assignedToLabel = patient.nurseUuid
        ? t('patients.standingAssignmentLabel', {
            name: formatNurseName(patient.nurse, t('patients.unknownNurse')),
          })
        : patient.currentShiftNurse
          ? t('patients.onShiftLabel', { name: patient.currentShiftNurse })
          : t('patients.notAssigned');

      return {
        id: patient.id,
        patientId: patient.patientUuid,
        status: patient.status,
        deletedAt: patient.deletedAt,
        patientName: patient.name || '-',
        departmentName: patient.departmentName,
        dateAdded: helpers.getDateAdded(patient.createdAt),
        nextVisit: patient.nextVisit?.start ? helpers.getDateAdded(patient.nextVisit.start) : '-',
        assignedToLabel,
      };
    });
  },
  shiftVisitsList(state, getters, rootState, rootGetters): PatientVisitListItem[] {
    const shifts =
      state.shiftVisits?.flatMap((visit) => {
        if ('visits' in visit) {
          return visit.visits as PatientVisit;
        }
        return visit as PatientVisit;
      }) || [];
    const allVisits: PatientVisitListItem[] = [];
    shifts.forEach((visit) => {
      const visitEnd: Date | null = visit.end ? new Date(visit.end) : null;
      const visitStart: Date | null = visit.start ? new Date(visit.start) : null;
      const durationMinutes =
        visitEnd && visitStart ? Math.round((visitEnd.getTime() - visitStart.getTime()) / (1000 * 60)) : 0;
      const hours = Math.floor(durationMinutes / 60);
      const minutes = durationMinutes % 60;
      const formatedDuration = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
      allVisits.push({
        key: visit.id,
        id: visit.id,
        dateAdded: helpers.getDateAdded(visit.start),
        startTime: helpers.getDateAdded(visit.start).helped,
        endTime: helpers.getDateAdded(visit.end).helped,
        duration: formatedDuration,
        patient_name: visit.patient?.name || '-',
        visitId: visit.checklist?.visit_uuid || '',
        patientId: visit.patient?.patientUuid || '',
        nurseName: rootGetters['users/usersList']?.filter((el) => el.id === visit.userUuid)[0]?.name || '-',
      });
    });
    return allVisits;
  },
  shiftVisitsPageInfo(state, getters) {
    return {
      count: getters.shiftVisitsList.length,
      limit: getters.getPaginationShowSize,
    };
  },
  nurseHistoryList(state, getters) {
    return state.nurseHistoryLocation?.map((el) => {
      return {
        name: el.name,
        userId: el.user_uuid,
        locations: el.locations,
        departmentName: el.departmentName,
        institutionName: el.institutionName,
        visitedPatients: el?.arrivals?.map((patient) => {
          const dateArrive: any = new Date(patient.time_arrive);
          const dateLeave: any = new Date(patient.time_leave);
          const durationMinutes = Math.round((dateLeave.getTime() - dateArrive.getTime()) / (1000 * 60));
          const hours = Math.floor(durationMinutes / 60);
          const minutes = durationMinutes % 60;
          const formatedDuration = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
          return {
            id: patient.uuid,
            patientName: patient.patient_name,
            address: patient.address || '',
            location: patient.location,
            timeArrive: `${dateArrive.getHours().toString().padStart(2, '0')}: ${dateArrive.getMinutes().toString().padStart(2, '0')}`,
            timeLeave: `${dateLeave.getHours().toString().padStart(2, '0')}: ${dateLeave.getMinutes().toString().padStart(2, '0')}`,
            duration: formatedDuration,
          };
        }),
      };
    });
  },
};

export default getters;
